/**
 * Issue #80 (slice 2) — «Параметры сделки» client-side helpers.
 * Mirrors server/src/lib/dealParams.ts (there is no shared package between
 * web and server, same mirroring as EDUCATIONAL_GENERAL_CONTRACTOR in
 * types/api.ts): the front-end runs the 4.1 sums check LIVE as a hint, the
 * server stays the only writer and the only blocker (at WIN).
 */
import type { DealParams } from '../types/api';

/** «С точностью до уровня тыс. руб.» — any difference below 1 000 ₽ passes. */
export const DEAL_SUM_TOLERANCE = 1000;

/** The canonical empty block — one writer (PATCH replaces as a whole), so
 *  the draft always carries the full shape. */
export function emptyDealParams(): DealParams {
  return {
    positions: [],
    discountMode: null,
    totalContractCost: null,
    robotCost: null,
    rtcCost: null,
    otherCost: null,
    payments: [],
    shipment: [],
    integrationDates: [],
    deliveryDate: null,
    closingAt: null,
  };
}

/** Deep clone of the stored block for the edit draft (or the empty block). */
export function draftFromStored(stored: DealParams | null | undefined): DealParams {
  if (!stored) return emptyDealParams();
  return {
    positions: stored.positions.map((p) => ({ ...p })),
    discountMode: stored.discountMode,
    totalContractCost: stored.totalContractCost,
    robotCost: stored.robotCost,
    rtcCost: stored.rtcCost,
    otherCost: stored.otherCost,
    payments: stored.payments.map((p) => ({ ...p })),
    shipment: stored.shipment.map((s) => ({ ...s })),
    integrationDates: [...stored.integrationDates],
    deliveryDate: stored.deliveryDate,
    closingAt: stored.closingAt,
  };
}

/** Issue #80 4.1 — the entered breakdown (Робот + РТК + иные услуги) vs the
 *  total contract cost, tolerance < 1 000 ₽. Runs only when BOTH sides are
 *  entered (missing total or a fully missing breakdown never warns); a
 *  partially entered breakdown counts the missing values as zero. Same rule
 *  as the server gate — the front hints, the server blocks at WIN. */
export function dealSumMismatch(
  dp: DealParams,
): { sum: number; total: number } | null {
  if (dp.totalContractCost == null) return null;
  const { robotCost, rtcCost, otherCost } = dp;
  if (robotCost == null && rtcCost == null && otherCost == null) return null;
  const sum = (robotCost ?? 0) + (rtcCost ?? 0) + (otherCost ?? 0);
  if (Math.abs(sum - dp.totalContractCost) < DEAL_SUM_TOLERANCE) return null;
  return { sum, total: dp.totalContractCost };
}

/** «Кол-во продаваемых позиций» — the educational simple variant counts the
 *  robots straight from the positions rows. */
export function positionsTotalCount(positions: DealParams['positions']): number {
  return positions.reduce((acc, p) => acc + p.quantity, 0);
}

/** Distinct non-empty position labels — offered as picks for «Дата отгрузки»
 *  rows (free text stays possible alongside). */
export function positionLabels(positions: DealParams['positions']): string[] {
  const seen = new Set<string>();
  for (const p of positions) {
    const label = p.robotType.trim();
    if (label) seen.add(label);
  }
  return [...seen];
}

/** True when nothing is entered anywhere — the read-only digest then shows a
 *  single «not filled» line instead of a wall of empty rows. */
export function dealParamsIsEmpty(dp: DealParams): boolean {
  return (
    dp.positions.length === 0 &&
    dp.discountMode === null &&
    dp.totalContractCost == null &&
    dp.robotCost == null &&
    dp.rtcCost == null &&
    dp.otherCost == null &&
    dp.payments.length === 0 &&
    dp.shipment.length === 0 &&
    dp.integrationDates.length === 0 &&
    dp.deliveryDate == null &&
    dp.closingAt == null
  );
}

/** Draft vs stored comparison — the Save button stays disabled until the
 *  user actually changes something (same UX as the «Генподрядчик» block). */
export function dealParamsEqual(a: DealParams, b: DealParams): boolean {
  return (
    a.discountMode === b.discountMode &&
    a.totalContractCost === b.totalContractCost &&
    a.robotCost === b.robotCost &&
    a.rtcCost === b.rtcCost &&
    a.otherCost === b.otherCost &&
    a.deliveryDate === b.deliveryDate &&
    a.closingAt === b.closingAt &&
    a.integrationDates.length === b.integrationDates.length &&
    a.integrationDates.every((d, i) => d === b.integrationDates[i]) &&
    a.positions.length === b.positions.length &&
    a.positions.every(
      (p, i) => p.robotType === b.positions[i].robotType && p.quantity === b.positions[i].quantity,
    ) &&
    a.payments.length === b.payments.length &&
    a.payments.every(
      (p, i) => p.amount === b.payments[i].amount && p.paidAt === b.payments[i].paidAt,
    ) &&
    a.shipment.length === b.shipment.length &&
    a.shipment.every(
      (s, i) =>
        s.positionLabel === b.shipment[i].positionLabel && s.shipAt === b.shipment[i].shipAt,
    )
  );
}

/**
 * Honest client-side pre-flight before the PATCH: the server trims labels
 * and rejects empty robot types, non-integer quantities, empty payment
 * amounts and empty shipment labels — fail fast with a human message instead
 * of letting the user hit a 422. Returns the first error's key, or null.
 */
export type DealParamsDraftError =
  | 'errPositionType'
  | 'errPositionQuantity'
  | 'errPaymentAmount'
  | 'errShipmentLabel'
  | 'errIntegrationDate';

export function dealParamsDraftError(dp: DealParams): DealParamsDraftError | null {
  for (const p of dp.positions) {
    if (!p.robotType.trim()) return 'errPositionType';
    if (!Number.isInteger(p.quantity) || p.quantity < 1) return 'errPositionQuantity';
  }
  for (const p of dp.payments) {
    if (!Number.isFinite(p.amount) || p.amount < 0) return 'errPaymentAmount';
  }
  for (const s of dp.shipment) {
    if (!s.positionLabel.trim()) return 'errShipmentLabel';
  }
  // The server rejects empty integration-date rows (every entry must be a
  // date) — an added-but-unfilled row blocks the save until filled/removed.
  for (const d of dp.integrationDates) {
    if (!d) return 'errIntegrationDate';
  }
  return null;
}
